import { UrlLoad } from "@pdfking/compatibility"
import type { StandardResponse } from "@pdfking/io"
import { ResponseAdopt, ResponseContentLength, StreamCreate, StreamToIterable } from "@pdfking/io"
import { MapAsync } from "@pdfking/tfunc"

import type { NetworkProgress, NetworkTrackingCallback } from "#public"

/**
 * Fetch with download progress tracking.
 *
 * Wraps UrlLoad to track download progress via callback.
 *
 * @param url - URL to fetch
 * @param options - Progress tracking options with download callback
 * @returns Promise resolving to Response with tracked stream
 *
 * @example
 * ```ts
 * import { ProgressFetch } from "@pdfking/pdfengine"
 *
 * const response = await ProgressFetch("data:text/plain,hello world", {
 *   download: ({ done, total }) => console.log(`${done}/${total}`)
 * })
 * await response.text() // "hello world"
 * ```
 */
export const ProgressFetch = (
  url: string,
  { download }: Omit<NetworkProgress, "upload"> = {}
): Promise<StandardResponse> => UrlLoad(url).then(r => trackResponse(r, download))

const trackStream = (
  sourceStream: ReadableStream<Uint8Array>,
  progress: NetworkTrackingCallback,
  cancel: () => void,
  content_length?: number
): ReadableStream<Uint8Array> => {
  let done = 0

  const send = () => progress({ done, total: content_length ?? done })

  void send()

  return StreamCreate(
    MapAsync((value: Uint8Array) => {
      done += value.byteLength
      void send()
      return value
    }, StreamToIterable(sourceStream)),
    cancel
  )
}

// Watching the body means replacing it, and a new Response knows only what its
// headers say — so what the loader worked out about the entry is carried over
// rather than derived a second time from a copy of the headers.
const trackResponse = (
  response: StandardResponse,
  progress?: NetworkTrackingCallback
): StandardResponse => {
  if (!progress || !response.body) return response

  return ResponseAdopt(
    new Response(
      trackStream(
        response.body,
        progress,
        () => response.body!.cancel(),
        ResponseContentLength(response)
      ),
      {
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
      }
    ),
    response.info
  )
}
